import { cn } from '#/utils/cn';
import {
  DIRECTION_LABEL,
  DIRECTION_TEXT_CLASS,
  directionOf,
} from '#/utils/direction';

import { ArrowDownIcon, ArrowUpIcon } from './icons';

type IndicatorBaseProps = {
  type: 'percent' | 'value';
  suffix?: string;
  /** What the value measures, announced ahead of the direction (e.g. 'Percent change'). */
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
};

const sizeClasses = {
  sm: 'text-static-12',
  md: 'text-static-14',
} as const;

// Ensure value OR oldValue/newValue
export type IndicatorProps =
  | (IndicatorBaseProps & { value: number; oldValue?: never; newValue?: never })
  | (IndicatorBaseProps & {
      oldValue: number;
      newValue: number;
      value?: never;
    });

export function Indicator({
  type,
  suffix,
  label,
  className,
  size = 'md',
  value,
  oldValue,
  newValue,
}: IndicatorProps) {
  // Number.isFinite guards against undefined/NaN/'' that Storybook sends for empty number controls
  const usesDiff = Number.isFinite(oldValue) && Number.isFinite(newValue);
  // toPrecision(10) strips floating-point drift (e.g. 102.15 - 100 = 2.1500000000000057)
  const computed = usesDiff
    ? parseFloat((newValue! - oldValue!).toPrecision(10))
    : (value ?? 0);

  const direction = directionOf(computed);
  const isPositive = direction === 'positive';
  const isNegative = direction === 'negative';
  const isZero = direction === 'unchanged';
  const isPercent = type === 'percent';
  const compact = size === 'sm';

  const formatted = isPositive ? `+${computed}` : `${computed}`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-4',
        sizeClasses[size],
        isPercent
          ? {
              'rounded px-8 py-4': !compact,
              'gap-2 rounded px-6 py-2': compact,
              'bg-positive-bg text-positive-fg': isPositive,
              'bg-negative-bg text-negative-fg': isNegative,
              'bg-unchanged-bg text-unchanged-fg': isZero,
            }
          : DIRECTION_TEXT_CLASS[direction],
        className,
      )}
    >
      {isPercent &&
        !isZero &&
        (isPositive ? (
          <ArrowUpIcon size={compact ? 10 : 14} aria-hidden="true" />
        ) : (
          <ArrowDownIcon size={compact ? 10 : 14} aria-hidden="true" />
        ))}
      <span className="sr-only">
        {label ? `${label}, ` : ''}
        {DIRECTION_LABEL[direction]}
      </span>
      <span>
        {formatted}
        {suffix}
      </span>
    </span>
  );
}
